import React, { useState, useEffect } from 'react';
import '../styles/AIEcosystemAnalysis.css';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export default function AIEcosystemAnalysis() {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('change');

  useEffect(() => {
    fetchAnalysis();
    const interval = setInterval(fetchAnalysis, 60000); // Refresh every 60 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchAnalysis = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/ai-ecosystem/analysis`);
      if (!response.ok) throw new Error('Failed to fetch analysis');
      const data = await response.json();
      setAnalysis(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching analysis:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="analysis-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading AI Ecosystem analysis...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analysis-container">
        <div className="error-box">
          <h2>Error Loading Analysis</h2>
          <p>{error}</p>
          <button onClick={fetchAnalysis} className="retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return <div className="analysis-container">No analysis data available</div>;
  }

  // Get stocks for selected category
  const getDisplayedStocks = () => {
    let stocks = [];
    
    if (selectedCategory === 'all') {
      Object.entries(analysis.ecosystem || {}).forEach(([category, categoryStocks]) => {
        Object.entries(categoryStocks).forEach(([symbol, data]) => {
          stocks.push({
            symbol,
            category,
            ...data
          });
        });
      });
    } else {
      const categoryStocks = analysis.ecosystem?.[selectedCategory] || {};
      Object.entries(categoryStocks).forEach(([symbol, data]) => {
        stocks.push({
          symbol,
          category: selectedCategory,
          ...data
        });
      });
    }

    // Sort stocks
    return stocks.sort((a, b) => {
      if (sortBy === 'change') {
        return (b.day_change_percent || 0) - (a.day_change_percent || 0);
      } else if (sortBy === 'price') {
        return (b.current_price || 0) - (a.current_price || 0);
      } else if (sortBy === 'symbol') {
        return a.symbol.localeCompare(b.symbol);
      }
      return 0;
    });
  };

  const displayedStocks = getDisplayedStocks();
  const sentiment = analysis.market_sentiment || {};

  return (
    <div className="analysis-container">
      {/* Header */}
      <div className="analysis-header">
        <h1>AI Ecosystem Analysis</h1>
        <p className="subtitle">Strategic analysis of 21 AI industry stocks across 5 ecosystem layers</p>
        <p className="timestamp">Last updated: {new Date(analysis.timestamp).toLocaleString()}</p>
      </div>

      {/* Market Sentiment Box */}
      <div className="sentiment-box">
        <h2>Market Sentiment</h2>
        <div className="sentiment-metrics">
          <div className="metric bullish">
            <div className="metric-label">Bullish</div>
            <div className="metric-value">{sentiment.bullish || 0}</div>
            <div className="metric-percent">{sentiment.bullish_percentage || 0}%</div>
          </div>
          <div className="metric bearish">
            <div className="metric-label">Bearish</div>
            <div className="metric-value">{sentiment.bearish || 0}</div>
          </div>
          <div className="metric neutral">
            <div className="metric-label">Total Analyzed</div>
            <div className="metric-value">{sentiment.total_analyzed || 0}</div>
          </div>
        </div>
      </div>

      {/* Key Findings */}
      {analysis.key_findings && analysis.key_findings.length > 0 && (
        <div className="findings-box">
          <h3>Key Findings</h3>
          <ul className="findings-list">
            {analysis.key_findings.map((finding, idx) => (
              <li key={idx} className="finding-item bullish">
                ✓ {finding}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Risk Factors */}
      {analysis.risk_factors && analysis.risk_factors.length > 0 && (
        <div className="findings-box">
          <h3>Risk Factors</h3>
          <ul className="findings-list">
            {analysis.risk_factors.map((risk, idx) => (
              <li key={idx} className="finding-item bearish">
                ⚠ {risk}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Portfolio Recommendations */}
      <div className="recommendations-box">
        <h2>Portfolio Allocation Strategies</h2>
        <div className="recommendations-grid">
          {(analysis.portfolio_recommendations || []).map((rec, idx) => (
            <div key={idx} className="recommendation-card">
              <h4>{rec.allocation}</h4>
              <div className="rec-section">
                <strong>Composition:</strong>
                <p>{rec.composition}</p>
              </div>
              <div className="rec-section">
                <strong>Expected Returns:</strong>
                <p className="returns">{rec.expected_yield}</p>
              </div>
              <div className="rec-section">
                <strong>Best For:</strong>
                <p>{rec.target_investor}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Category Filter */}
      <div className="filter-section">
        <h3>Stock Details by Category</h3>
        <div className="filter-buttons">
          <button
            className={`filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('all')}
          >
            All (21)
          </button>
          {Object.keys(analysis.ecosystem || {}).map((category) => (
            <button
              key={category}
              className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="sort-section">
          <label>Sort by:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select">
            <option value="change">Price Change %</option>
            <option value="price">Current Price</option>
            <option value="symbol">Symbol A-Z</option>
          </select>
        </div>
      </div>

      {/* Stocks Table */}
      <div className="stocks-table-wrapper">
        <table className="stocks-table">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Category</th>
              <th>Price</th>
              <th>Change %</th>
              <th>Signal</th>
              <th>P/E Ratio</th>
              <th>Recent News</th>
            </tr>
          </thead>
          <tbody>
            {displayedStocks.map((stock) => (
              <tr key={stock.symbol} className="stock-row">
                <td className="symbol-cell">
                  <strong>{stock.symbol}</strong>
                  <div className="stock-name">{stock.name}</div>
                </td>
                <td>
                  <span className={`category-badge ${stock.category.toLowerCase().replace(/\s+/g, '-')}`}>
                    {stock.category}
                  </span>
                </td>
                <td className="price-cell">${stock.current_price?.toFixed(2) || 'N/A'}</td>
                <td className={`change-cell ${stock.day_change_percent > 0 ? 'positive' : stock.day_change_percent < 0 ? 'negative' : 'neutral'}`}>
                  {stock.day_change_percent > 0 ? '+' : ''}{stock.day_change_percent?.toFixed(2) || 0}%
                </td>
                <td className="signal-cell">
                  {stock.latest_signal ? (
                    <span className={`signal-badge ${stock.latest_signal.regime.toLowerCase()}`}>
                      {stock.latest_signal.regime}
                    </span>
                  ) : (
                    <span className="no-signal">—</span>
                  )}
                </td>
                <td className="pe-cell">{stock.pe_ratio?.toFixed(1) || 'N/A'}</td>
                <td className="news-cell">
                  {stock.recent_news_count > 0 ? (
                    <span className="news-badge">{stock.recent_news_count}</span>
                  ) : (
                    <span className="no-news">None</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Category Legend */}
      <div className="legend-box">
        <h3>Ecosystem Categories</h3>
        <div className="legend-grid">
          <div className="legend-item">
            <span className="legend-color core-ai"></span>
            <strong>Core AI (5)</strong>: NVDA, MSFT, GOOGL, META, TSLA — The GPU and AI platform leaders
          </div>
          <div className="legend-item">
            <span className="legend-color data-centers"></span>
            <strong>Data Centers (4)</strong>: EQIX, DLR, PLD, CCI — REITs providing compute infrastructure
          </div>
          <div className="legend-item">
            <span className="legend-color networking"></span>
            <strong>Networking (5)</strong>: AVGO, QCOM, AMD, VZ, T — Network chips and 5G backbone
          </div>
          <div className="legend-item">
            <span className="legend-color power"></span>
            <strong>Power (4)</strong>: NEE, DUK, PPL, AQN.TO — Utilities providing 50-100MW per facility
          </div>
          <div className="legend-item">
            <span className="legend-color cooling"></span>
            <strong>Cooling (3)</strong>: HON, MMM, ITW — Thermal management and HVAC systems
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="info-box">
        <h3>About This Analysis</h3>
        <p>
          This analysis tracks 21 stocks across the complete AI supply chain — from GPUs and cloud platforms to the power, 
          cooling, and data center infrastructure required to run AI models at scale.
        </p>
        <p>
          <strong>Use Case:</strong> Identify diversified exposure to AI infrastructure beyond just chip stocks. 
          Build resilient portfolios with companies that benefit from AI growth at different price points and risk levels.
        </p>
      </div>
    </div>
  );
}
